import NetInfo from '@react-native-community/netinfo';
import { getIpData } from '../api/external-endpoints';
import { DEFAULT_COUNTRY_CODE, DEFAULT_CALLING_CODE } from '../CONSTANTS';
import { getCallingCode } from 'react-native-country-picker-modal';
import { getCountryByPhoneNumber } from 'rn-international-phone-number';
import DeviceCountry, { TYPE_TELEPHONY } from 'react-native-device-country';
import { Platform } from 'react-native';
import { currencyForCountryCode, normalizeCurrencyCode } from './currency.ts';

export interface CountryDetectionResult {
  countryCode: string;
  country: string;
  callingCode: string;
  currency?: string; // ISO 4217 e.g., "KES"
}

const getDefaultCountry = (): CountryDetectionResult => ({
  countryCode: DEFAULT_COUNTRY_CODE,
  country: 'Kenya',
  callingCode: DEFAULT_CALLING_CODE,
  currency: 'KES',
});

/**
 * Gets country info from IP address (used for iOS)
 */
export const getCountryFromIp = async (): Promise<CountryDetectionResult> => {
  try {
    const netInfo = await NetInfo.fetch();
    if (
      netInfo.isConnected &&
      netInfo.details &&
      'ipAddress' in netInfo.details
    ) {
      const ipAddress = netInfo.details?.ipAddress as string;
      console.log(ipAddress, 'ipAddress');

      if (ipAddress) {
        const ipData = await getIpData(ipAddress);
        if (ipData && ipData.country_code) {
          const countryCode = ipData.country_code;
          const rawCallingCode =
            ipData.country_calling_code || `+${DEFAULT_CALLING_CODE}`;
          const callingCode = rawCallingCode.replace('+', '');
          const countryObj = getCountryByPhoneNumber(`+${callingCode}`) as any;
          const country = (countryObj && countryObj.name) || 'Unknown';
          const currency = normalizeCurrencyCode(ipData.currency, 'KES');
          if (country) {
            console.log(country, 'CountryFromIp');
            return {
              countryCode: countryCode,
              country: country,
              callingCode: callingCode,
              currency,
            };
          }
        } else {
          return getDefaultCountry();
        }
      }
    }

    return getDefaultCountry();
  } catch (error) {
    console.log(error, 'getCountryFromIp error');
    return getDefaultCountry();
  }
};

/**
 * Gets country info from device telephony
 */
export const getCountryFromDevice =
  async (): Promise<CountryDetectionResult> => {
    try {
      // Web builds can end up with `react-native-device-country` undefined/no-op.
      // Also: the library relies on native APIs; don't call it on web.
      if (
        Platform.OS === 'web' ||
        !DeviceCountry ||
        typeof (DeviceCountry as any).getCountryCode !== 'function'
      ) {
        return await getCountryFromIp();
      }

      const result = await DeviceCountry.getCountryCode(TYPE_TELEPHONY);
      console.log(result, 'DeviceCountry result');
      if (result && result.code) {
        const countryCode = result.code.toUpperCase();
        const callingCode = await getCallingCode(countryCode as any);
        const countryObj = getCountryByPhoneNumber(
          `+${String(callingCode || '').replace(/^\+/, '')}`,
        ) as any;
        const country = (countryObj && countryObj.name) || 'Unknown';
        console.log(callingCode, 'callingCode from DeviceCountry');
        return {
          countryCode: countryCode,
          country: country,
          callingCode: callingCode,
          currency: await currencyForCountryCode(countryCode, 'KES'),
        };
      }

      // Fallback to IP detection
      return await getCountryFromIp();
    } catch (error) {
      console.log(error, 'getCountryFromDevice error');
      // Fallback to IP detection
      return await getCountryFromIp();
    }
  };

/**
 * Main function to detect country using DeviceCountry
 */
export const detectCountry = async (): Promise<CountryDetectionResult> => {
  try {
    if (Platform.OS === 'web') {
      return await getCountryFromIp();
    }

    if (Platform.OS === 'ios') {
      return await getCountryFromIp();
    } else {
      return await getCountryFromDevice();
    }
  } catch (error) {
    console.log(error, 'detectCountry error');
    // Ultimate fallback to ip detection
    return getCountryFromIp();
  }
};
